import React, { useEffect, useMemo } from "react";
import { Stack, Typography, Divider, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { createTeacher, updateTeacher } from "../redux/actions/teacherActions";
import { getBranches } from "../redux/actions/branchActions";
import { teacherActions } from "../redux/slices/teacher";
import { cleanFormData } from "../helpers/formHelpers";

import { TeacherResponseDto } from "../shared/dtos/teacher/TeacherResponseDto";
import { TeacherCreateRequestDto } from "../shared/dtos/teacher/TeacherCreateRequestDto";
import { Gender } from "../shared/enums/Gender";

const addressSchema = Yup.object().shape({
  streetLine1: Yup.string().required("Street line 1 is required"),
  streetLine2: Yup.string().nullable(),
  city: Yup.string().required("City is required"),
  stateOrProvince: Yup.string().required("State/Province is required"),
  postalCode: Yup.string().required("Postal Code is required"),
  country: Yup.string().required("Country is required"),
});

const teacherSchema = Yup.object().shape({
  branchId: Yup.string().required("Branch is required"),
  firstName: Yup.string().trim().required("First Name is required"),
  middleName: Yup.string().trim().nullable(),
  lastName: Yup.string().trim().required("Last Name is required"),
  email: Yup.string()
    .trim()
    .email("Invalid email address")
    .required("Email is required"),
  dateOfJoining: Yup.string().required("Date of joining is required"),
  primaryPhoneNumber: Yup.string().required("Primary phone number is required"),
  qualifications: Yup.string().required("Qualifications are required"),
  gender: Yup.mixed<Gender>()
    .oneOf(Object.values(Gender))
    .required("Gender is required"),
  address: addressSchema,
});

interface CreateOrUpdateTeacherFormProps {
  teacher?: TeacherResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateTeacherForm: React.FC<CreateOrUpdateTeacherFormProps> = ({
  teacher = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { items: branches } = useAppSelector((state) => state.branch);

  const isSuperAdmin = userInfo?.userRole === "SUPER_ADMIN";

  useEffect(() => {
    if (isSuperAdmin && !branches.length) dispatch(getBranches());
  }, [branches.length, dispatch, isSuperAdmin]);

  const branchOptions = useMemo(
    () => branches.map((branch) => ({ value: branch.id, label: branch.name })),
    [branches],
  );

  const genderOptions = useMemo(
    () =>
      Object.values(Gender).map((g) => ({
        value: g,
        label: g.charAt(0) + g.slice(1).toLowerCase(),
      })),
    [],
  );

  const initialValues: TeacherCreateRequestDto = {
    branchId: teacher?.branchId || (!isSuperAdmin && userInfo?.branchId) || "",
    firstName: teacher?.firstName || "",
    middleName: teacher?.middleName || "",
    lastName: teacher?.lastName || "",
    email: teacher?.email || "",
    dateOfJoining: teacher?.dateOfJoining
      ? teacher.dateOfJoining.substring(0, 10)
      : "",
    primaryPhoneNumber: teacher?.primaryPhoneNumber || "",
    qualifications: teacher?.qualifications || "",
    gender: (teacher?.gender as Gender) || Gender.MALE,
    address: {
      streetLine1: teacher?.address?.streetLine1 || "",
      streetLine2: teacher?.address?.streetLine2 || "",
      city: teacher?.address?.city || "",
      stateOrProvince: teacher?.address?.stateOrProvince || "",
      postalCode: teacher?.address?.postalCode || "",
      country: teacher?.address?.country || "Kenya",
    },
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(teacherActions.setErrorUpdate(null))
            : () => dispatch(teacherActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={teacherSchema}
        onSubmit={(values) => {
          const cleanedValues = cleanFormData(
            values,
          ) as TeacherCreateRequestDto;

          if (isEditing && teacher) {
            dispatch(updateTeacher({ id: teacher.id, values: cleanedValues }));
          } else {
            dispatch(createTeacher(cleanedValues));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<TeacherCreateRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  textAlign: "left",
                  fontWeight: 700,
                }}
              >
                Teacher Details
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                }}
              >
                <FormTextField
                  name="firstName"
                  label="First Name"
                  disabled={isEditing}
                />
                <FormTextField
                  name="middleName"
                  label="Middle Name (Optional)"
                  disabled={isEditing}
                />
                <FormTextField
                  name="lastName"
                  label="Last Name"
                  disabled={isEditing}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: isEditing ? "1fr 1fr" : "1fr",
                  },
                }}
              >
                {isSuperAdmin && (
                  <Box>
                    <FormSelectField
                      name="branchId"
                      label="Branch"
                      options={branchOptions}
                      disabled={isEditing}
                    />
                  </Box>
                )}
                <FormTextField
                  name="email"
                  label="Email Address"
                  type="email"
                  disabled={isEditing}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="dateOfJoining"
                  label="Date of Joining"
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <FormSelectField
                  name="gender"
                  label="Gender"
                  options={genderOptions}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="primaryPhoneNumber"
                  label="Primary Phone Number"
                  placeholder="+254711223344"
                />
                <FormTextField
                  name="qualifications"
                  label="Qualifications"
                  placeholder="e.g. B.Ed Science"
                />
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  textAlign: "left",
                  fontWeight: 700,
                }}
              >
                Address Details
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="address.streetLine1"
                  label="Street Line 1"
                />
                <FormTextField
                  name="address.streetLine2"
                  label="Street Line 2 (Optional)"
                />
                <FormTextField name="address.city" label="City" />
                <FormTextField
                  name="address.stateOrProvince"
                  label="State/Province"
                />
                <FormTextField name="address.postalCode" label="Postal Code" />
                <FormTextField name="address.country" label="Country" />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Teacher Profile"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <PersonAddOutlinedIcon />
                }
                sx={{ mt: 2 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateTeacherForm;
